import { useEffect, type JSX } from 'react';
import type { Id } from '../../../core/model';
import { Dialog } from '../../components/Dialog';
import { useProject, useStudio } from '../../context';
import { attempt } from '../../safe';
import { describe } from '../properties/PropertiesDialog';
import { PropertyForm } from '../properties/PropertyForm';

export function TransitionPropertiesDialog({ transitionId, onClose }: { transitionId: Id; onClose(): void }): JSX.Element | null {
  const studio = useStudio();
  const config = useProject((p) => p.transitions[transitionId]);
  const exists = config !== undefined;
  useEffect(() => {
    if (!exists) onClose();
  }, [exists, onClose]);
  if (!config) return null;

  const def = studio.registry.getTransition(config.kind);
  const form = describe(() => (def ? def.properties(config.settings) : []));
  return (
    <Dialog
      title={`Properties for "${config.name}"`}
      onClose={onClose}
      width={520}
      footer={
        <>
          <button
            type="button"
            className="ui-btn"
            style={{ marginRight: 'auto' }}
            disabled={!def}
            onClick={() => def && attempt(studio, () => studio.commands.updateTransition(transitionId, { settings: def.defaults() }))}
          >
            Defaults
          </button>
          <button type="button" className="ui-btn primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {!def ? <div className="ui-warn-text">The transition type "{config.kind}" is not available.</div> : null}
      {form.error ? <div className="ui-error-text">Could not build the settings form: {form.error}</div> : null}
      {def && !form.error ? (
        <PropertyForm
          descriptors={form.descriptors}
          settings={config.settings}
          onChange={(key, value) =>
            attempt(studio, () => studio.commands.updateTransition(transitionId, { settings: { ...config.settings, [key]: value } }))
          }
          onCommit={() => studio.project.sealHistory()}
        />
      ) : null}
    </Dialog>
  );
}
